<script setup lang="ts">
/**
 * La chiamata in arrivo sul Cruscotto (2026-10-28): un collega ti chiama, e qui squilla.
 *
 * È la metà web della chiamata diretta (`SquilliService` sul server). Il Cruscotto un filo in
 * tempo reale non ce l'ha, quindi **chiede**: ogni `SQUILLO.ascoltoMs` (tre secondi), anche a
 * scheda nascosta — una chiamata che squilla dietro un'altra scheda deve farsi sentire, ed è il
 * browser a rallentare da sé le schede nascoste da molto. Una domanda è una riga cercata per
 * indice: costa niente.
 *
 * Quando squilla: una finestra in alto a destra con chi chiama e due tasti, una suoneria fatta col
 * browser (due toni, come un telefono; niente file da scaricare) e il titolo della scheda che
 * lampeggia. «Rispondi» porta nella stanza; «Rifiuta» lo dice a chi chiama. Se chi chiama
 * riattacca, o lo squillo scade, la finestra se ne va da sola al giro dopo.
 *
 * La suoneria la può bloccare il browser finché non si è toccato niente nella pagina: è una regola
 * del browser e non si aggira. La finestra e il titolo restano.
 */

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { Phone, PhoneOff, Video } from '@lucide/vue';
import Button from 'primevue/button';
import { SQUILLO, type SquilloVista } from '@bestehr/shared';
import { videochiamate } from '@/lib/videochiamate';

const router = useRouter();
const squillo = ref<SquilloVista | null>(null);
const decidendo = ref(false);
const errore = ref<string | null>(null);

let timer: ReturnType<typeof setTimeout> | null = null;
let inVolo: AbortController | null = null;
let spenta = false;

async function ascolta(): Promise<void> {
  if (spenta || decidendo.value) return;
  inVolo?.abort();
  const c = new AbortController();
  inVolo = c;
  try {
    const s = await videochiamate.inArrivo(c.signal);
    if (!c.signal.aborted) squillo.value = s;
  } catch {
    // Un errore non si mostra: è un ascolto. Il giro dopo riprova.
  }
}

function programma(): void {
  if (timer) clearTimeout(timer);
  // Smontata mentre un ascolto era in volo: il suo `finally` richiamava qui e riarmava un giro che
  // nessuno spegneva piu' (uno in piu' a ogni uscita e rientro dal Cruscotto).
  if (spenta) return;
  timer = setTimeout(() => {
    void ascolta().finally(programma);
  }, SQUILLO.ascoltoMs);
}

function suVisibilita(): void {
  if (document.visibilityState === 'visible') void ascolta();
}

onMounted(() => {
  void ascolta();
  programma();
  document.addEventListener('visibilitychange', suVisibilita);
});

onBeforeUnmount(() => {
  spenta = true;
  if (timer) clearTimeout(timer);
  inVolo?.abort();
  document.removeEventListener('visibilitychange', suVisibilita);
  zitto();
});

/* ------------------------------------------------------------------ la suoneria */

let audio: AudioContext | null = null;
let suoneria: ReturnType<typeof setInterval> | null = null;
let titoloPrima: string | null = null;
let lampo: ReturnType<typeof setInterval> | null = null;

/** Due toni brevi, poi silenzio: il ritmo di un telefono, a volume basso. */
function squillaUnaVolta(): void {
  if (audio === null) return;
  const ora = audio.currentTime;
  for (const [inizio, freq] of [
    [0, 880],
    [0.18, 660],
    [0.5, 880],
    [0.68, 660],
  ] as const) {
    const o = audio.createOscillator();
    const g = audio.createGain();
    o.type = 'sine';
    o.frequency.value = freq;
    g.gain.setValueAtTime(0, ora + inizio);
    g.gain.linearRampToValueAtTime(0.12, ora + inizio + 0.02);
    g.gain.linearRampToValueAtTime(0, ora + inizio + 0.16);
    o.connect(g).connect(audio.destination);
    o.start(ora + inizio);
    o.stop(ora + inizio + 0.18);
  }
}

function suona(nome: string): void {
  try {
    audio ??= new AudioContext();
    void audio.resume();
    squillaUnaVolta();
    suoneria = setInterval(squillaUnaVolta, 2500);
  } catch {
    // Senza audio: restano la finestra e il titolo.
  }
  titoloPrima = document.title;
  let acceso = false;
  lampo = setInterval(() => {
    acceso = !acceso;
    document.title = acceso ? `● ${nome} ti chiama` : (titoloPrima ?? '');
  }, 1000);
}

function zitto(): void {
  if (suoneria) clearInterval(suoneria);
  suoneria = null;
  if (lampo) clearInterval(lampo);
  lampo = null;
  if (titoloPrima !== null) document.title = titoloPrima;
  titoloPrima = null;
}

watch(
  () => squillo.value?.id ?? null,
  (id, prima) => {
    if (id === prima) return;
    zitto();
    errore.value = null;
    if (id !== null && squillo.value) suona(squillo.value.chiamante.nome);
  },
);

/* ------------------------------------------------------------------ i due gesti */

const chi = computed(() => squillo.value?.chiamante ?? null);

async function rispondi(): Promise<void> {
  const s = squillo.value;
  if (!s || decidendo.value) return;
  decidendo.value = true;
  zitto();
  try {
    const esito = await videochiamate.rispondi(s.id);
    squillo.value = null;
    if (esito.stato === 'risposta') {
      void router.push({ name: 'chiamata', params: { codice: esito.codice }, query: { subito: '1' } });
    } else {
      errore.value = esito.stato === 'annullata' ? 'Ha riattaccato prima che rispondessi.' : 'La chiamata non squilla più.';
    }
  } catch {
    errore.value = 'Non sono riuscito a rispondere. Riprova.';
  } finally {
    decidendo.value = false;
  }
}

async function rifiuta(): Promise<void> {
  const s = squillo.value;
  if (!s || decidendo.value) return;
  decidendo.value = true;
  zitto();
  try {
    await videochiamate.rifiuta(s.id);
  } catch {
    // Rifiutata o no, la finestra se ne va: chi chiama la vedra' scadere.
  } finally {
    squillo.value = null;
    decidendo.value = false;
  }
}
</script>

<template>
  <Transition name="arrivo">
    <section
      v-if="squillo && chi"
      class="arrivo"
      role="alertdialog"
      aria-labelledby="arrivo-chi"
      aria-describedby="arrivo-cosa"
      data-testid="chiamata-in-arrivo"
    >
      <span class="arrivo__segno" aria-hidden="true">
        <Video :size="20" :stroke-width="2" />
        <span class="arrivo__polso" />
      </span>
      <div class="arrivo__testo">
        <p id="arrivo-chi" class="arrivo__chi">{{ chi.nome }}</p>
        <p id="arrivo-cosa" class="arrivo__cosa">
          ti chiama in videochiamata<template v-if="chi.reparto"> · {{ chi.reparto }}</template>
        </p>
        <p v-if="errore" class="arrivo__errore" role="status">{{ errore }}</p>
      </div>
      <div class="arrivo__tasti">
        <Button
          severity="danger"
          outlined
          label="Rifiuta"
          :disabled="decidendo"
          data-testid="arrivo-rifiuta"
          @click="rifiuta"
        >
          <template #icon><PhoneOff :size="16" :stroke-width="2" aria-hidden="true" /></template>
        </Button>
        <Button label="Rispondi" :loading="decidendo" data-testid="arrivo-rispondi" @click="rispondi">
          <template #icon><Phone :size="16" :stroke-width="2" aria-hidden="true" /></template>
        </Button>
      </div>
    </section>
  </Transition>
</template>

<style scoped>
.arrivo {
  position: fixed;
  top: calc(var(--beste-layout-topbar) + var(--beste-space-12));
  right: var(--beste-space-16);
  z-index: var(--beste-z-toast);
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  grid-template-areas:
    'segno testo'
    'tasti tasti';
  gap: var(--beste-space-12) var(--beste-space-12);
  width: min(24rem, calc(100vw - 2 * var(--beste-space-16)));
  padding: var(--beste-space-16);
  border-radius: var(--beste-radius-lg);
  border: 1px solid var(--beste-border-subtle);
  background: var(--beste-surface-raised);
  color: var(--beste-text-primary);
  box-shadow: var(--beste-elevation-overlay);
}

.arrivo__segno {
  grid-area: segno;
  position: relative;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: var(--beste-radius-full);
  background: var(--beste-accent-solid);
  color: var(--beste-text-onSolid);
}

.arrivo__segno > svg {
  position: relative;
  z-index: 1;
}

.arrivo__polso {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: var(--beste-accent-solid);
  animation: polso 1.6s var(--beste-ease-standard) infinite;
}

@keyframes polso {
  0% {
    transform: scale(1);
    opacity: 0.5;
  }
  70%,
  100% {
    transform: scale(1.7);
    opacity: 0;
  }
}

.arrivo__testo {
  grid-area: testo;
  min-width: 0;
  align-self: center;
}

.arrivo__chi {
  margin: 0;
  font-weight: var(--beste-font-bodyStrong-weight);
  font-size: var(--beste-font-bodyStrong-size);
  overflow-wrap: anywhere;
}

.arrivo__cosa,
.arrivo__errore {
  margin: 0;
  font-size: var(--beste-font-small-size);
  color: var(--beste-text-secondary);
}

.arrivo__errore {
  color: var(--beste-status-danger-text);
}

.arrivo__tasti {
  grid-area: tasti;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--beste-space-8);
}

.arrivo-enter-active,
.arrivo-leave-active {
  transition:
    opacity var(--beste-duration-normal) var(--beste-ease-standard),
    transform var(--beste-duration-normal) var(--beste-ease-standard);
}

.arrivo-enter-from,
.arrivo-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (prefers-reduced-motion: reduce) {
  .arrivo__polso {
    animation: none;
  }

  .arrivo-enter-active,
  .arrivo-leave-active {
    transition: none;
  }
}
</style>
